import { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { format } from 'date-fns';
import { AlertCircle, Check, Loader, Send, ShieldCheck, X } from 'lucide-react';
import type { BuildSchematic } from '../../types/buildTeam';
import type { SchematicDelivery, SchematicDeliveryServers } from '../../types/buildSchematicDelivery';
import { buildSchematicDeliveryApi } from '../../services/buildSchematicDeliveryApi';
import { canDeliverSchematic, deliveryIsActive, mergeSchematicDeliveries, SchematicDeliveryRequests } from '../../utils/buildSchematicDelivery';
import { schematicDisplayTitle } from '../../utils/buildSchematic';
import { useOnClickOutside } from '../../hooks/useOnClickOutside';
import { cn } from '../../utils/cn';

let deliveryRequests: SchematicDeliveryRequests | undefined;
function getDeliveryRequests(): SchematicDeliveryRequests {
  if (!deliveryRequests) {
    let storage: Storage | undefined;
    try { storage = window.sessionStorage; } catch { /* Fall back to tab memory. */ }
    deliveryRequests = new SchematicDeliveryRequests(storage);
  }
  return deliveryRequests;
}

export function SchematicDeliveryAction({ schematic, canSend, compact = false }: {
  schematic: BuildSchematic;
  canSend: boolean;
  compact?: boolean;
}) {
  const [open, setOpen] = useState(false);
  if (!canSend || !canDeliverSchematic(schematic)) return null;
  return (
    <>
      <button
        type="button"
        className={compact ? 'btn-icon border-glass-200/10' : 'glass-button-sm touch-manipulation'}
        title="Send to server"
        aria-label={`Send ${schematicDisplayTitle(schematic)} to server`}
        aria-haspopup="dialog"
        onClick={(event) => { event.stopPropagation(); setOpen(true); }}
      >
        <Send className="w-3.5 h-3.5" />
        {!compact && 'Send to server'}
      </button>
      {open && <SchematicDeliveryModal schematic={schematic} onClose={() => setOpen(false)} />}
    </>
  );
}

export function SchematicDeliveryHistory({ deliveries }: { deliveries: SchematicDelivery[] }) {
  return (
    <div className="space-y-2">
      <h3 className="text-sm font-medium text-white">Delivery history</h3>
      {deliveries.length === 0 ? <p className="text-xs text-glass-500">This schematic has not been sent to a server yet.</p> : (
        <ul className="divide-y divide-glass-200/10">
          {deliveries.map((delivery) => (
            <li key={delivery.id} className="py-3 first:pt-0 space-y-1">
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm text-white break-all">{delivery.server_address}</span>
                <span className={cn('badge shrink-0', delivery.state === 'succeeded' ? 'badge-success' : delivery.state === 'failed' ? 'badge-danger' : 'badge-info')}>
                  {deliveryIsActive(delivery) ? <Loader className="w-3 h-3 animate-spin" /> : delivery.state === 'succeeded' ? <Check className="w-3 h-3" /> : <AlertCircle className="w-3 h-3" />}
                  {delivery.state === 'succeeded' ? 'Delivered' : delivery.state === 'failed' ? 'Failed' : delivery.state === 'running' ? 'Delivering' : 'Queued'}
                </span>
              </div>
              {delivery.filename && <p className="font-mono text-xs text-glass-400 break-all">{delivery.filename}</p>}
              <time className="text-xs text-glass-500" dateTime={new Date(delivery.updated_at * 1000).toISOString()}>
                {format(new Date(delivery.updated_at * 1000), 'PPp')}
              </time>
              {delivery.state === 'succeeded' && <p className="text-xs text-glass-400">Saved in this server’s WorldEdit schematics folder.</p>}
              {delivery.error && <p className="text-xs text-red-400 break-words">{delivery.error}</p>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function SchematicDeliveryModal({ schematic, onClose }: { schematic: BuildSchematic; onClose: () => void }) {
  const titleId = useId();
  const selectId = useId();
  const modalRef = useRef<HTMLDivElement>(null);
  const sendingRef = useRef(false);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useOnClickOutside(modalRef, onClose);

  const [inventory, setInventory] = useState<SchematicDeliveryServers | null>(null);
  const [deliveries, setDeliveries] = useState<SchematicDelivery[]>([]);
  const [selectedAddress, setSelectedAddress] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [serversError, setServersError] = useState<string | null>(null);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [sendError, setSendError] = useState<string | null>(null);
  const [refresh, setRefresh] = useState(0);

  useEffect(() => {
    const previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    modalRef.current?.focus();
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') { event.preventDefault(); closeRef.current(); }
      if (event.key !== 'Tab') return;
      const focusable = modalRef.current?.querySelectorAll<HTMLElement>('button:not(:disabled), select:not(:disabled), [tabindex="0"]');
      if (!focusable?.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && (document.activeElement === first || document.activeElement === modalRef.current)) {
        event.preventDefault(); last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault(); first.focus();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => { document.removeEventListener('keydown', handleKeyDown); previousFocus?.focus(); };
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    let timer: number | undefined;
    async function poll() {
      const [servers, history] = await Promise.allSettled([
        buildSchematicDeliveryApi.servers(controller.signal),
        buildSchematicDeliveryApi.history(schematic.id, controller.signal),
      ]);
      if (controller.signal.aborted) return;
      if (servers.status === 'fulfilled') {
        setInventory(servers.value);
        setServersError(null);
      } else {
        setServersError(servers.reason instanceof Error ? servers.reason.message : 'Could not load CDU servers.');
      }
      if (history.status === 'fulfilled') {
        setDeliveries((current) => mergeSchematicDeliveries(current, history.value));
        setHistoryError(null);
      } else {
        setHistoryError(history.reason instanceof Error ? history.reason.message : 'Could not refresh delivery history.');
      }
      setLoading(false);
      timer = window.setTimeout(poll, 5000);
    }
    void poll();
    return () => { controller.abort(); window.clearTimeout(timer); };
  }, [schematic.id, refresh]);

  const selectedServer = inventory?.servers.find((server) => server.server_address === selectedAddress);
  const selectedDeliveries = deliveries.filter((delivery) => delivery.server_address === selectedAddress);
  const active = selectedDeliveries.some(deliveryIsActive);
  const tooLarge = inventory !== null && schematic.size > inventory.limits.max_compressed_bytes;
  const disabled = loading || sending || !selectedServer?.available || active || tooLarge || !!serversError || !!historyError;

  const handleSend = async () => {
    if (disabled || sendingRef.current) return;
    sendingRef.current = true;
    setSending(true);
    setSendError(null);
    try {
      const requests = getDeliveryRequests();
      const requestId = requests.prepare(schematic.id, selectedAddress, deliveries);
      const delivery = await buildSchematicDeliveryApi.send(schematic.id, selectedAddress, requestId);
      requests.acknowledge(schematic.id, selectedAddress, requestId, delivery.id);
      setDeliveries((current) => mergeSchematicDeliveries(current, [delivery]));
    } catch (error) {
      setSendError(error instanceof Error ? error.message : 'Could not submit delivery. Please try again.');
    } finally {
      sendingRef.current = false;
      setSending(false);
    }
  };

  return createPortal(
    <div className="modal-overlay z-[9999]">
      <div ref={modalRef} tabIndex={-1} className="modal-panel max-w-xl" role="dialog" aria-modal="true" aria-labelledby={titleId}>
        <div className="modal-header">
          <div className="min-w-0 pr-6">
            <h2 id={titleId} className="modal-title flex items-center gap-2"><Send className="w-5 h-5 text-accent-300" />Send schematic to server</h2>
            <p className="modal-subtitle break-words">{schematicDisplayTitle(schematic)}</p>
          </div>
          <button type="button" className="modal-close" onClick={onClose} aria-label="Close delivery" title="Close"><X className="w-4 h-4" /></button>
        </div>
        <div className="modal-body space-y-4">
          <p className="text-sm text-glass-400">The CDU agent saves a validated copy in the selected server’s WorldEdit schematics folder. Nothing is pasted into the world automatically.</p>
          <div className="subcard p-3 flex items-start gap-2 text-xs text-glass-400">
            <ShieldCheck className="w-4 h-4 shrink-0 text-accent-300" />
            <div className="space-y-1">
              <p>WorldEdit Sponge (.schem) and MCEdit (.schematic) formats only. File contents and size are checked before delivery.</p>
              <p>Blocks only: entities and block entity data (including chest inventories and sign text) are rejected, as are command blocks, structure blocks, jigsaw blocks and spawners. If rejected, re-export without these contents.</p>
              {inventory && <p>Maximum {(inventory.limits.max_compressed_bytes / 1024 / 1024).toFixed(0)} MiB uploaded; {(inventory.limits.max_uncompressed_bytes / 1024 / 1024).toFixed(0)} MiB after decompression.</p>}
            </div>
          </div>
          <div>
            <label htmlFor={selectId} className="text-xs font-medium text-glass-400 mb-1.5 block">CDU server</label>
            <select id={selectId} value={selectedAddress} onChange={(event) => { setSelectedAddress(event.target.value); setSendError(null); }} disabled={loading || sending} className="glass-input w-full">
              <option value="">{loading ? 'Loading servers…' : 'Choose a server'}</option>
              {inventory?.servers.map((server) => <option key={server.server_address} value={server.server_address} disabled={!server.available}>{server.display_name} ({server.server_address}){!server.available ? ` — ${server.reason || 'Unavailable'}` : ''}</option>)}
            </select>
            {inventory?.servers.length === 0 && <p className="text-xs text-glass-400 mt-2">No CDU servers are configured for schematic delivery yet.</p>}
            {inventory && inventory.servers.length > 0 && !inventory.servers.some((server) => server.available) && <p className="text-xs text-glass-400 mt-2">No server is currently available. Unavailable servers show the reason in the list.</p>}
            {selectedServer && !selectedServer.available && <p className="text-xs text-amber-300 mt-2">{selectedServer.reason || 'This server is currently unavailable.'}</p>}
          </div>
          {tooLarge && <p className="alert alert-danger" role="alert">This upload exceeds the server delivery size limit. Export a smaller schematic and upload it again.</p>}
          {(serversError || historyError) && <div className="alert alert-danger" role="alert"><AlertCircle /><div className="space-y-2">{serversError && <p>{serversError}</p>}{historyError && <p>{historyError}</p>}<button type="button" className="glass-button-sm" onClick={() => setRefresh((value) => value + 1)}>Refresh</button></div></div>}
          {sendError && <div className="alert alert-danger" role="alert"><AlertCircle /><div><p>{sendError}</p><p className="mt-1 text-xs">If the response was interrupted, retrying checks the same request without creating another copy.</p></div></div>}
          {loading ? <p className="text-sm text-glass-400 flex items-center gap-2"><Loader className="w-4 h-4 animate-spin" />Loading delivery history…</p> : <div aria-live="polite"><SchematicDeliveryHistory deliveries={deliveries} /></div>}
          <p className="text-xs text-glass-500">Status refreshes while this window is open. Queued deliveries continue after closing it.</p>
        </div>
        <div className="modal-footer">
          <button type="button" className="glass-button-secondary" onClick={onClose}>Close</button>
          <button type="button" className="btn-primary" disabled={disabled} onClick={handleSend}>
            {sending || active ? <Loader className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            {sending ? 'Validating…' : active ? 'Delivery in progress' : sendError ? 'Retry request' : selectedDeliveries[0]?.state === 'succeeded' ? 'Send another copy' : selectedDeliveries[0]?.state === 'failed' ? 'Retry delivery' : 'Send to server'}
          </button>
        </div>
      </div>
    </div>, document.body,
  );
}
